/**
 * StatCard Component
 * Enhanced statistics card with Flowbite, trend indicators, and visual polish
 */

import { Card } from 'flowbite-react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';

export default function StatCard({
  title,
  value,
  icon,
  trend,
  color = 'primary',
  showSparkline = false
}) {
  // Color theme configurations
  const colorConfig = {
    primary: {
      gradient: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      trendUp: 'text-green-600 bg-green-50',
      trendDown: 'text-red-600 bg-red-50'
    },
    success: {
      gradient: 'from-green-500 to-green-600',
      bg: 'bg-green-50',
      text: 'text-green-600',
      trendUp: 'text-green-600 bg-green-50',
      trendDown: 'text-red-600 bg-red-50'
    },
    warning: {
      gradient: 'from-yellow-500 to-yellow-600',
      bg: 'bg-yellow-50',
      text: 'text-yellow-600',
      trendUp: 'text-green-600 bg-green-50',
      trendDown: 'text-red-600 bg-red-50'
    },
    danger: {
      gradient: 'from-red-500 to-red-600',
      bg: 'bg-red-50',
      text: 'text-red-600',
      trendUp: 'text-green-600 bg-green-50',
      trendDown: 'text-red-600 bg-red-50'
    }
  };

  const theme = colorConfig[color] || colorConfig.primary;
  const trendDirection = trend?.direction || 'up';
  const TrendIcon = trendDirection === 'up' ? ArrowUpIcon : ArrowDownIcon;
  const trendColorClass = trendDirection === 'up' ? theme.trendUp : theme.trendDown;

  return (
    <Card
      className="transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer border-0"
    >
      <div className="flex items-start justify-between">
        {/* Left content */}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            {title}
          </p>
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-bold text-gray-900">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          </div>

          {/* Trend indicator */}
          {trend && (
            <div className="mt-3 flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${trendColorClass}`}>
                <TrendIcon className="h-3 w-3" />
                {trend.value}
              </span>
              {trend.label && (
                <span className="text-sm text-gray-600">
                  {trend.label}
                </span>
              )}
            </div>
          )}

          {/* Mini sparkline placeholder */}
          {showSparkline && (
            <div className="mt-4 h-12 flex items-end gap-1">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-t ${theme.bg} opacity-40`}
                  style={{
                    height: `${Math.random() * 100}%`,
                    minHeight: '20%'
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Icon with gradient background */}
        {icon && (
          <div className="flex-shrink-0">
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${theme.gradient} shadow-lg`}
            >
              <div className="text-white text-2xl">
                {icon}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.node,
  trend: PropTypes.shape({
    direction: PropTypes.oneOf(['up', 'down']),
    value: PropTypes.string,
    label: PropTypes.string
  }),
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'danger']),
  showSparkline: PropTypes.bool
};
